/**
 * The fork's hosted chat-history web API. sphinx has no `/api/web/*` routes —
 * every function here is served from Postgres through the data seam
 * (`@/data/chat-history`). The module survives as the shape its callers (the
 * chat client, the history rail, the promote-to-eval dialog) already import.
 */
import {
  fetchChatHistoryDetail,
  fetchChatHistoryList,
  runChatSessionAction,
} from "@/data/chat-history";
import type {
  McpToolResultImageRenderingPolicy,
  ModelVisibleMcpToolResults,
} from "@/lib/client-config-v2";

/**
 * Per-message shape inside the JSON pointed at by `messagesBlobUrl`.
 * The backend currently produces `{ role, content, ... }`; once Convex begins
 * stamping `senderUserId` on user-role messages, the transcript reader will
 * surface it directly via this type.
 */
export interface PersistedChatMessage {
  id?: string;
  role?: string;
  content?: unknown;
  /** Set by the backend on newly appended user messages once shipped. */
  senderUserId?: string;
  [key: string]: unknown;
}

export interface ChatHistorySession {
  _id: string;
  chatSessionId: string;
  projectId?: string;
  customTitle?: string;
  firstMessagePreview: string;
  status: "active" | "archived";
  directVisibility: "private" | "project";
  modelId?: string;
  modelSource?: string;
  messageCount: number;
  version: number;
  startedAt: number;
  lastActivityAt: number;
  userId?: string;
  guestExternalId?: string;
  isPinned: boolean;
  pinnedAt?: number;
  lastReadAt?: number;
  manualUnread: boolean;
  isUnread: boolean;
}

export interface ChatHistoryListResponse {
  ok: boolean;
  personal: ChatHistorySession[];
  project: ChatHistorySession[];
}

export interface ResumeConfig {
  systemPrompt?: string;
  temperature?: number;
  requireToolApproval?: boolean;
  respectToolVisibility?: boolean;
  modelVisibleMcpToolResults?: ModelVisibleMcpToolResults;
  mcpToolResultImageRendering?: McpToolResultImageRenderingPolicy;
  selectedServers?: string[];
}

export interface ChatHistoryDetailSession extends ChatHistorySession {
  messagesBlobUrl: string | null;
  usedServerIds?: string[];
  resumeConfig?: ResumeConfig;
}

export interface ChatHistoryWidgetSnapshot {
  _id: string;
  sessionId: string;
  toolCallId: string;
  toolName: string;
  serverId: string;
  uiType: "mcp-apps" | "openai-apps";
  resourceUri?: string;
  widgetCsp: Record<string, unknown> | null;
  widgetPermissions: Record<string, unknown> | null;
  widgetPermissive: boolean;
  prefersBorder: boolean;
  widgetHtmlUrl?: string | null;
  toolOutputUrl?: string | null;
}

export interface ChatHistoryTurnTrace {
  turnId: string;
  promptIndex: number;
  startedAt: number;
  endedAt: number;
  finishReason?: string;
  usage?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
  };
  spanCount: number;
  modelId?: string;
  spansBlobUrl?: string | null;
}

export interface ChatHistoryDetailResponse {
  ok: boolean;
  session: ChatHistoryDetailSession;
  widgetSnapshots?: ChatHistoryWidgetSnapshot[];
  turnTraces?: ChatHistoryTurnTrace[];
}

/**
 * The fork's per-request `headers` / `signal`. sphinx resolves every call
 * locally, so both are accepted and ignored — callers (the history rail, the
 * scenario chat client) still pass them.
 */
interface ChatHistoryRequestOptions {
  headers?: HeadersInit;
  signal?: AbortSignal;
}

/** `projectId` / paging are ignored: sphinx has one project and no archive. */
export async function listChatHistory(
  _params: {
    projectId?: string;
    status: "active" | "archived";
    limit?: number;
    before?: number;
  },
  _requestOptions?: ChatHistoryRequestOptions
): Promise<ChatHistoryListResponse> {
  return { ok: true, ...(await fetchChatHistoryList()) };
}

/** `projectId` is ignored: sphinx has one project. */
export async function getChatHistoryDetail(
  params: {
    sessionId?: string;
    chatSessionId: string;
    projectId?: string;
  },
  _requestOptions?: ChatHistoryRequestOptions
): Promise<ChatHistoryDetailResponse> {
  return fetchChatHistoryDetail(params.chatSessionId);
}

export async function chatHistoryAction(
  action: string,
  sessionId: string,
  params?: Record<string, unknown>,
  _requestOptions?: ChatHistoryRequestOptions
): Promise<{ ok: boolean }> {
  await runChatSessionAction(action, sessionId, params);
  return { ok: true };
}
